import { createI18n } from 'vue-i18n'
import en from '@/locales/en.json'
import fr from '@/locales/fr.json'
import { safeGet, safeSet } from '@/lib/safeStorage'

// Define available locales
export const SUPPORTED_LOCALES = ['en', 'fr'] as const
export type Locale = typeof SUPPORTED_LOCALES[number]

// Locale information for UI display
export const LOCALE_INFO = {
  en: {
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸'
  },
  fr: {
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷'
  }
} as const

// Get browser locale or fallback
function getBrowserLocale(): Locale {
  const browserLocale = navigator.language.split('-')[0]
  return SUPPORTED_LOCALES.includes(browserLocale as Locale) 
    ? browserLocale as Locale 
    : 'en'
}

// Get stored locale from localStorage or use browser default
function getInitialLocale(): Locale {
  const stored = safeGet('hudstore-locale')
  if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
    return stored as Locale
  }
  return getBrowserLocale()
}

// Store locale in localStorage
export function setLocale(locale: Locale) {
  safeSet('hudstore-locale', locale)
}

// Create i18n instance
const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: getInitialLocale(),
  fallbackLocale: 'en',
  messages: {
    en,
    fr
  },
  // Enable missing key warnings in development
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
  // Production missing-key handler: log to Sentry once per key per session so a
  // missing French translation in prod surfaces as a fixable error instead of
  // a silent raw-key render in the UI. Returns undefined → falls back to `en`.
  missing: (locale, key) => {
    if (import.meta.env.PROD && typeof window !== 'undefined') {
      const seenKey = `__i18n_missing_${locale}_${key}`
      if (!(window as unknown as Record<string, boolean>)[seenKey]) {
        (window as unknown as Record<string, boolean>)[seenKey] = true
        import('@sentry/vue').then(({ captureMessage }) => {
          captureMessage(`i18n.missing.${locale}.${key}`, 'warning')
        }).catch(() => { /* sentry not loaded — drop silently */ })
      }
    }
    return undefined
  }
})

export default i18n

// Helper function to check if a locale is supported
export function isLocaleSupported(locale: string): locale is Locale {
  return SUPPORTED_LOCALES.includes(locale as Locale)
}

// Get current locale
export function getCurrentLocale(): Locale {
  return i18n.global.locale.value as Locale
}

// Change locale and persist it
export function changeLocale(locale: Locale) {
  if (!isLocaleSupported(locale)) {
    if (import.meta.env.DEV) {
      console.error(`Locale ${locale} is not supported`)
    }
    return
  }
  
  i18n.global.locale.value = locale
  setLocale(locale)
  
  // Update document language
  document.documentElement.lang = locale
}

// Get user's preferred language from auth store if logged in
export async function getUserPreferredLocale(): Promise<Locale | null> {
  try {
    // Dynamic import to avoid circular dependency
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()
    // Priority: user's lang > preferred_lang > organization_lang
    const userLang = authStore.user?.lang || authStore.user?.preferred_lang || authStore.user?.organization_lang
    if (userLang && isLocaleSupported(userLang)) {
      return userLang as Locale
    }
    return null
  } catch {
    return null
  }
}

// Initialize locale based on authentication state
export async function initializeLocale() {
  try {
    // First try to get user's preferred language if authenticated
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()
    
    if (authStore.isAuthenticated) {
      // Priority: user's lang > preferred_lang > organization_lang
      const userLang = authStore.user?.lang || authStore.user?.preferred_lang || authStore.user?.organization_lang
      if (userLang && isLocaleSupported(userLang)) {
        changeLocale(userLang as Locale)
        return
      }
    }
    
    // Fallback to stored locale or browser default
    const currentLang = getCurrentLocale()
    const storedLang = getInitialLocale()
    
    if (currentLang !== storedLang) {
      changeLocale(storedLang)
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to initialize locale:', error)
    }
    // Fallback to browser default
    changeLocale(getInitialLocale())
  }
}